import React, { useEffect, useState } from 'react';

import { SystemChangelogItem } from './types';
import { axios } from '../api';
import { ChangelogList } from '../component/ChangelogList';
import { TextInput } from '../component/TextInput';
import { Button } from '../component/buttons/Button';
import { IS_LOCAL } from '../config';

interface Script {
  label: string;
  description: string;
  command: string;
}

interface ScriptCategory {
  category: string;
  scripts: Script[];
}

const SCRIPT_CATALOGUE: ScriptCategory[] = [
  {
    category: 'Products',
    scripts: [
      {
        label: 'Create or Update Products',
        description: 'Create or update products from a CSV file uploaded to s3',
        command:
          'python manage.py add_product_to_db --file_prefix <FILE_PREFIX> --lousy_input <true|false>',
      },
      {
        label: 'Mark Product Returns As Expired',
        description: 'Mark Product Returns As Expired',
        command: 'python manage.py mark_product_returns_as_expired',
      },
    ],
  },
  {
    category: 'Accounts',
    scripts: [
      {
        label: 'Send Survey Responses to Gladly',
        description: 'Retrieve AskNicely responses and creates corresponding tasks in Gladly',
        command: 'python manage.py send_survey_responses_to_gladly --interval=30 --limit=100',
      },
      {
        label: 'Send Surveys',
        description: 'Request AskNicely to send surveys to customers',
        command: 'python manage.py send_surveys --charge_offset=11',
      },
      {
        label: 'Payment Method Expiring Soon',
        description: 'Send Payment Method Expiring Soon emails',
        command: 'python manage.py payment_method_expiring_soon',
      },
      {
        label: 'Finalize Stalled Fulfillments',
        description: 'Script to finalize stalled fulfillments',
        command: 'python manage.py finalize_stalled_fulfillments',
      },
      {
        label: 'Send Upcoming Order Reminders',
        description: 'Send upcoming order reminder emails',
        command: 'python manage.py send_upcoming_order_reminders',
      },
    ],
  },
  {
    category: 'Financial',
    scripts: [
      {
        label: 'Initiate Payments',
        description: 'Initiate Daily Order Lifecycle Job',
        command: 'python manage.py initiate_payments',
      },
      {
        label: 'Alarm Payment Life Cycle Job Finishes',
        description: 'Log the last order processed by the payment life cycle',
        command: 'python manage.py alarm_payment_life_cycle_job_finishes',
      },
      {
        label: 'Delete PII',
        description: 'Delete PII of a cancelled account',
        command: 'python manage.py delete_pii --email=<email>',
      },
      {
        label: 'Order to Cash',
        description: 'Send fulfillments to Netsuite for O2C processing',
        command: 'python manage.py order_to_cash_v2',
      },
    ],
  },
  {
    category: 'Data & Maintenance',
    scripts: [
      {
        label: 'Run DB Migrations',
        description: 'Apply pending Django database migrations',
        command: 'python manage.py migrate',
      },
      {
        label: 'Purge Cache Table',
        description: 'Remove rows that expired before today from the cache_table',
        command: 'python manage.py purge_cache_table',
      },
    ],
  },
];

export const CommandRunnerDashboard = () => {
  const [command, setCommand] = useState('');
  const [loading, setLoading] = useState(false);
  const [logsUrl, setLogsUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changelog, setChangelog] = useState<SystemChangelogItem[]>([]);
  const [changelogLoading, setChangelogLoading] = useState(true);
  const [changelogError, setChangelogError] = useState('');

  useEffect(() => {
    axios
      .get('system_changelog/commands')
      .then((response) => {
        setChangelog([...response.data].reverse());
      })
      .catch(() => {
        setChangelogError('Failed to load recent runs.');
      })
      .finally(() => {
        setChangelogLoading(false);
      });
  }, []);

  const handleRun = async () => {
    if (IS_LOCAL) {
      setError('Command execution is disabled in local environments.');
      return;
    }
    setLoading(true);
    setLogsUrl(null);
    setError(null);
    try {
      const response = await axios.post('run_command', { command });
      setLogsUrl(response.data.logs_url);
    } catch (err: unknown) {
      const message = (err as any)?.response?.data?.error ?? 'Something went wrong.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleScriptSelect = (script: Script) => {
    setCommand(script.command);
    setLogsUrl(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-[calc(100vh-77px)] bg-slate50 p-8">
      <div className="mx-auto max-w-5xl">
        {/* Page header */}
        <h1>
          <b>Command Runner</b>
        </h1>

        {/* Command Runner card */}
        <div className="rounded-lg border bg-white mb-8 mt-6">
          <div className="px-6 pt-6 pb-4">
            <h2>
              <b>Run a Command</b>
            </h2>
          </div>
          <div className="border-t px-6 py-4">
            <div className="flex gap-3">
              <TextInput
                value={command}
                setTextChange={setCommand}
                placeholder="E.g., python manage.py add_product_to_db --file_prefix ancillary_bundle"
                disabled={loading}
                className="flex-1 font-mono text-sm"
                onEnter={!command.trim() || loading ? undefined : handleRun}
              />
              <Button
                onClick={handleRun}
                isDisabled={!command.trim() || loading}
                label={loading ? 'Running...' : 'Run'}
              />
            </div>
            {logsUrl && (
              <p className="mt-3 text-sm">
                <a
                  href={logsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline"
                >
                  View logs
                </a>
              </p>
            )}
            {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
          </div>
        </div>

        {/* Script Catalogue */}
        <div className="rounded-lg border bg-white mb-6">
          <div className="px-6 pt-6 pb-4">
            <h2>
              <b>Script Catalogue</b>
            </h2>
          </div>
          <div className="border-t px-6 py-4 space-y-6">
            {SCRIPT_CATALOGUE.map((cat) => (
              <div key={cat.category}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest">
                  {cat.category}
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {cat.scripts.map((script) => (
                    <button
                      key={script.label}
                      onClick={() => handleScriptSelect(script)}
                      className="text-left rounded-lg border bg-white p-4 hover:bg-slate-50 transition-colors"
                    >
                      <p className="text-sm font-semibold text-slate-800">{script.label}</p>
                      <p className="mt-1 text-xs text-slate-500">{script.description}</p>
                      <p className="mt-2 truncate font-mono text-xs text-slate-400">
                        {script.command}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Runs */}
        <div className="mt-2 max-h-[500px] bg-white border rounded-lg p-4">
          <div className="pb-3">
            <h2>
              <b>Recent Runs</b>
            </h2>
          </div>
          <ChangelogList
            type="system"
            isLoading={changelogLoading}
            changelogError={changelogError}
            changelogItems={changelog}
          />
        </div>
      </div>
    </div>
  );
};
